export type DisplayVariant = "d1";
export type HeadlineVariant = "h1" | "h2" | "h3" | "h4" | "h5" | "h6";
export type ParagraphVariant = "paragraph-lg" | "paragraph-md" | "paragraph-sm";
export type ComponentVariant =
  | "careers-section-title"
  | "careers-job-location"
  | "wib-card-name"
  | "highlight-card-title"
  | "highlight-card-title-lg"
  | "highlight-card-title-xl"
  | "wib-card-body"
  | "configurator-title"
  | "speech-bubble"
  | "mini-subtitle"
  | "turntable-hint"
  | "film-caption"
  | "command-pill"
  | "language-phrase"
  | "language-name"
  | "lockup-bubble"
  | "story-year"
  | "story-line"
  | "serene-poster"
  | "serene-chapter"
  | "serene-intro"
  | "serene-italic";
export type TextVariant =
  DisplayVariant | HeadlineVariant | ParagraphVariant | ComponentVariant;
export type EyebrowVariant =
  | "eyebrow-xl"
  | "eyebrow-lg"
  | "eyebrow-md"
  | "eyebrow-sm"
  | "eyebrow-highlight-card"
  | "eyebrow-highlight-card-lg"
  | "eyebrow-highlight-card-xl"
  | "eyebrow-accent"
  | "eyebrow-section";

// Each variant bundles family + weight + size/leading/tracking so a size can
// never be applied without its intended companions. Classes are emitted on the
// element itself: fontFace.scss has global h1–h5/p styles that override
// inherited values but never element-attached classes.
export const TEXT_VARIANT_CLASSES: Record<TextVariant, string> = {
  d1: "font-ppMori font-semibold text-d1",
  h1: "font-ppMori font-semibold text-h1",
  h2: "font-ppMori font-semibold text-h2",
  h3: "font-ppMori font-semibold text-h3",
  h4: "font-ppMori font-semibold text-h4",
  h5: "font-ppMori font-semibold text-h5",
  h6: "font-ppMori font-semibold text-h6",
  "paragraph-lg": "font-ppMori font-normal text-paragraph-lg",
  "paragraph-md": "font-ppMori font-normal text-paragraph-md",
  "paragraph-sm": "font-ppMori font-normal text-paragraph-sm",
  "careers-section-title":
    "font-ppMori font-semibold text-careers-section-title",
  "careers-job-location": "font-ppMori font-normal text-careers-job-location",
  "wib-card-name": "font-ppMori font-semibold text-wib-card-name",
  "highlight-card-title": "font-ppMori font-semibold text-highlight-card-title",
  "highlight-card-title-lg":
    "font-ppMori font-semibold text-highlight-card-title-lg",
  "highlight-card-title-xl":
    "font-ppMori font-semibold text-highlight-card-title-xl",
  "wib-card-body": "font-ppMori font-normal text-wib-card-body",
  "configurator-title": "font-ppMori font-semibold text-configurator-title",
  "speech-bubble": "font-ppMori font-semibold text-speech-bubble",
  "mini-subtitle": "font-ppMori font-semibold text-mini-subtitle",
  "turntable-hint": "font-ppMori font-normal text-turntable-hint",
  "film-caption": "font-ppMori font-semibold text-film-caption",
  "command-pill": "font-ppMori font-semibold text-command-pill",
  "language-phrase": "font-ppMori font-semibold text-language-phrase",
  "language-name": "font-ppMori font-normal text-language-name",
  "lockup-bubble": "font-ppMori font-semibold text-lockup-bubble",
  "story-year": "font-ppMori font-semibold text-story-year",
  "story-line": "font-ppMori font-semibold text-story-line",
  /* /serene (src/views/serene). The light headlines set their emphasis in
     medium italic. */
  "serene-poster": "font-ppMori font-light text-serene-poster",
  "serene-chapter": "font-ppMori font-light text-serene-chapter",
  "serene-intro": "font-ppMori font-light lowercase text-serene-intro",
  "serene-italic": "font-ppMori font-light italic text-serene-italic",
};

export const EYEBROW_VARIANT_CLASSES: Record<EyebrowVariant, string> = {
  "eyebrow-xl": "font-trimMono font-medium uppercase text-eyebrow-xl",
  "eyebrow-lg": "font-trimMono font-medium uppercase text-eyebrow-lg",
  "eyebrow-md": "font-trimMono font-medium uppercase text-eyebrow-md",
  "eyebrow-sm": "font-trimMono font-medium uppercase text-eyebrow-sm",
  "eyebrow-highlight-card":
    "font-trimMono font-medium uppercase text-highlight-card-eyebrow",
  "eyebrow-highlight-card-lg":
    "font-trimMono font-medium uppercase text-highlight-card-eyebrow-lg",
  "eyebrow-highlight-card-xl":
    "font-trimMono font-medium uppercase text-highlight-card-eyebrow-xl",
  "eyebrow-accent": "font-trimMono font-medium uppercase text-eyebrow-accent",
  "eyebrow-section": "font-trimMono font-medium uppercase text-eyebrow-section",
};

export type TextTone = "primary" | "secondary" | "inverse" | "inherit";

export const TONE_CLASSES: Record<TextTone, string> = {
  primary: "text-content-primary",
  secondary: "text-content-secondary",
  inverse: "text-content-inverse",
  inherit: "text-inherit",
};
